import {
  Document,
  Packer,
  Paragraph,
  TextRun as DocxTextRun,
  HeadingLevel,
  Table,
  TableRow as DocxTableRow,
  TableCell as DocxTableCell,
  WidthType,
  AlignmentType,
  convertInchesToTwip,
  LevelFormat,
  BorderStyle,
  Footer,
  Header,
  PageNumber,
  PageBreak,
  ShadingType,
  ImageRun,
  PageOrientation,
  UnderlineType,
} from 'docx';
import type {
  IStylesOptions,
  ISectionOptions,
  INumberingOptions,
} from 'docx';
import { safeSaveAs } from './safariDownload';
import { XMLParser } from 'fast-xml-parser';
import type { DocumentJSON, DocumentSection, DocumentElement } from '../types';

// ============================================================================
// Constants and Defaults
// ============================================================================

// DXA units: 1440 DXA = 1 inch
const DXA_PER_INCH = 1440;
const DEFAULT_MARGIN = DXA_PER_INCH; // 1 inch

// US Letter size in DXA
const US_LETTER_WIDTH = 12240;  // 8.5 inches
const US_LETTER_HEIGHT = 15840; // 11 inches

// Content width with 1 inch margins
const CONTENT_WIDTH = US_LETTER_WIDTH - (2 * DEFAULT_MARGIN); // 9360 DXA

// Default fonts and sizes (sizes in half-points: 24 = 12pt)
const DEFAULT_FONT = 'Arial';
const DEFAULT_SIZE = 24; // 12pt

// Default heading styles
interface HeadingStyleInternal {
  font: string;
  size: number;
  bold: boolean;
  spacing: { before: number; after: number };
}

const DEFAULT_HEADING_STYLES: Record<number, HeadingStyleInternal> = {
  1: { font: DEFAULT_FONT, size: 32, bold: true, spacing: { before: 240, after: 240 } },
  2: { font: DEFAULT_FONT, size: 28, bold: true, spacing: { before: 200, after: 120 } },
  3: { font: DEFAULT_FONT, size: 26, bold: true, spacing: { before: 160, after: 80 } },
  4: { font: DEFAULT_FONT, size: 24, bold: true, spacing: { before: 120, after: 60 } },
  5: { font: DEFAULT_FONT, size: 22, bold: true, spacing: { before: 80, after: 40 } },
  6: { font: DEFAULT_FONT, size: 20, bold: true, spacing: { before: 60, after: 20 } },
};

// Default table formatting
const DEFAULT_TABLE_BORDER = { style: BorderStyle.SINGLE, size: 4, color: 'CCCCCC' };
const DEFAULT_CELL_PADDING = { top: 80, bottom: 80, left: 120, right: 120 };
const DEFAULT_HEADER_BACKGROUND = 'D5E8F0';

// Map heading levels to docx HeadingLevel
const HEADING_LEVEL_MAP: Record<number, (typeof HeadingLevel)[keyof typeof HeadingLevel]> = {
  1: HeadingLevel.HEADING_1,
  2: HeadingLevel.HEADING_2,
  3: HeadingLevel.HEADING_3,
  4: HeadingLevel.HEADING_4,
  5: HeadingLevel.HEADING_5,
  6: HeadingLevel.HEADING_6,
};

// Map alignment strings to docx AlignmentType
const ALIGNMENT_MAP: Record<string, typeof AlignmentType[keyof typeof AlignmentType]> = {
  left: AlignmentType.LEFT,
  center: AlignmentType.CENTER,
  right: AlignmentType.RIGHT,
  justify: AlignmentType.JUSTIFIED,
};

// Highlight color type for docx
type HighlightColor = "black" | "blue" | "cyan" | "darkBlue" | "darkCyan" | "darkGray" | "darkGreen" | "darkMagenta" | "darkRed" | "darkYellow" | "green" | "lightGray" | "magenta" | "red" | "white" | "yellow" | "none";

// Map highlight color names to docx highlight colors
const HIGHLIGHT_COLOR_MAP: Record<string, HighlightColor> = {
  yellow: "yellow",
  green: "green",
  cyan: "cyan",
  magenta: "magenta",
  blue: "blue",
  red: "red",
  darkBlue: "darkBlue",
  darkCyan: "darkCyan",
  darkGreen: "darkGreen",
  darkMagenta: "darkMagenta",
  darkRed: "darkRed",
  darkYellow: "darkYellow",
  gray: "darkGray",
  lightGray: "lightGray",
  black: "black",
  white: "white",
};

// ============================================================================
// XML Parser Configuration
// ============================================================================

const xmlParserOptions = {
  ignoreAttributes: false,
  attributeNamePrefix: '@_',
  textNodeName: '#text',
  preserveOrder: true,
  trimValues: true,
  parseTagValue: false,
  parseAttributeValue: false,
};

// ============================================================================
// Unit Conversion Helpers (moved from xmlDocumentParser)
// ============================================================================

/**
 * Convert human-readable unit to DXA
 * Supports: "1in", "1.5in", "72pt", "2.5cm", "25mm", or raw number
 */
export function parseUnit(value: string | number | undefined, defaultValue: number): number {
  if (value === undefined || value === null) return defaultValue;
  if (typeof value === 'number') return value;

  const str = String(value).trim().toLowerCase();

  // Inches: "1in", "1.5in"
  const inchMatch = str.match(/^([\d.]+)\s*in$/);
  if (inchMatch) {
    return Math.round(parseFloat(inchMatch[1]) * DXA_PER_INCH);
  }

  // Points: "12pt", "14pt"
  const ptMatch = str.match(/^([\d.]+)\s*pt$/);
  if (ptMatch) {
    // 1 point = 20 DXA (twips)
    return Math.round(parseFloat(ptMatch[1]) * 20);
  }

  // Centimeters: "2.5cm"
  const cmMatch = str.match(/^([\d.]+)\s*cm$/);
  if (cmMatch) {
    // 1 inch = 2.54 cm
    return Math.round((parseFloat(cmMatch[1]) / 2.54) * DXA_PER_INCH);
  }

  // Millimeters: "25mm"
  const mmMatch = str.match(/^([\d.]+)\s*mm$/);
  if (mmMatch) {
    return Math.round((parseFloat(mmMatch[1]) / 25.4) * DXA_PER_INCH);
  }

  // Raw number (assumed DXA)
  const numValue = parseFloat(str);
  if (!isNaN(numValue)) return Math.round(numValue);

  return defaultValue;
}

/**
 * Parse percentage widths to DXA values
 * e.g., "25% 50% 25%" with totalWidth=9360 => [2340, 4680, 2340]
 */
export function parsePercentageWidths(widthsStr: string, totalWidth: number = CONTENT_WIDTH): number[] {
  const parts = widthsStr.trim().split(/\s+/);
  const widths: number[] = [];

  for (const part of parts) {
    const percentMatch = part.match(/^([\d.]+)%$/);
    if (percentMatch) {
      const percent = parseFloat(percentMatch[1]);
      widths.push(Math.round((percent / 100) * totalWidth));
    } else if (part.toLowerCase() === 'auto') {
      // Will be calculated after all fixed widths
      widths.push(-1);
    } else {
      // Try parsing as DXA or unit
      widths.push(parseUnit(part, Math.floor(totalWidth / parts.length)));
    }
  }

  // Handle 'auto' widths
  const fixedTotal = widths.filter(w => w > 0).reduce((a, b) => a + b, 0);
  const autoCount = widths.filter(w => w < 0).length;
  if (autoCount > 0) {
    const autoWidth = Math.floor((totalWidth - fixedTotal) / autoCount);
    for (let i = 0; i < widths.length; i++) {
      if (widths[i] < 0) widths[i] = autoWidth;
    }
  }

  // Adjust to ensure exact sum
  const currentSum = widths.reduce((a, b) => a + b, 0);
  if (currentSum !== totalWidth && widths.length > 0) {
    widths[widths.length - 1] += (totalWidth - currentSum);
  }

  return widths;
}

/**
 * Parse font size from various formats to half-points
 * "12pt" => 24, "14" => 28 (assumes pt if no unit), raw number => as-is
 */
function parseFontSize(value: string | number | undefined): number | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value === 'number') return value;

  const str = String(value).trim().toLowerCase();

  // Points: "12pt"
  const ptMatch = str.match(/^([\d.]+)\s*pt$/);
  if (ptMatch) {
    return Math.round(parseFloat(ptMatch[1]) * 2); // Convert to half-points
  }

  // Raw number (assume points, convert to half-points)
  const numValue = parseFloat(str);
  if (!isNaN(numValue)) {
    return Math.round(numValue * 2);
  }

  return undefined;
}

// ============================================================================
// XML Inline Content Parser
// ============================================================================

interface TextRunData {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strike?: boolean;
  color?: string;
  fontSize?: number;
  font?: string;
  highlight?: string;
}

/**
 * Parse mixed content (text with inline tags) into TextRun array
 * Input: "Hello <b>bold</b> and <i>italic</i> world"
 * Output: [{ text: "Hello " }, { text: "bold", bold: true }, ...]
 */
function parseInlineContent(nodes: unknown[]): TextRunData[] {
  const runs: TextRunData[] = [];

  for (const node of nodes) {
    if (!node || typeof node !== 'object') continue;
    const nodeObj = node as Record<string, unknown>;

    // Text node
    if ('#text' in nodeObj) {
      const text = String(nodeObj['#text']);
      if (text) {
        runs.push({ text });
      }
      continue;
    }

    // Element node - get the tag name (first key that's not #text or starts with @_)
    const tagName = Object.keys(nodeObj).find(k => !k.startsWith('@_') && k !== '#text' && k !== ':@');
    if (!tagName) continue;

    const tagContent = nodeObj[tagName] as unknown[];
    const attrs = (nodeObj[':@'] || {}) as Record<string, string>;

    // Parse children recursively
    const childRuns = tagContent ? parseInlineContent(tagContent) : [];

    // Apply formatting based on tag
    switch (tagName.toLowerCase()) {
      case 'b':
      case 'strong':
        for (const run of childRuns) {
          run.bold = true;
        }
        break;
      case 'i':
      case 'em':
        for (const run of childRuns) {
          run.italic = true;
        }
        break;
      case 'u':
        for (const run of childRuns) {
          run.underline = true;
        }
        break;
      case 's':
      case 'strike':
        for (const run of childRuns) {
          run.strike = true;
        }
        break;
      case 'span':
        for (const run of childRuns) {
          if (attrs['@_color']) run.color = attrs['@_color'].replace('#', '');
          if (attrs['@_font']) run.font = attrs['@_font'];
          if (attrs['@_size']) run.fontSize = parseFontSize(attrs['@_size']);
          if (attrs['@_highlight']) run.highlight = attrs['@_highlight'];
        }
        break;
    }

    runs.push(...childRuns);
  }

  return runs;
}

/**
 * Get text content from a node (flattening all children)
 */
function getTextContent(nodes: unknown[]): string {
  const runs = parseInlineContent(nodes);
  return runs.map(r => r.text).join('');
}

/**
 * Check if content has inline formatting tags
 */
function hasInlineFormatting(nodes: unknown[]): boolean {
  for (const node of nodes) {
    if (!node || typeof node !== 'object') continue;
    const nodeObj = node as Record<string, unknown>;

    for (const key of Object.keys(nodeObj)) {
      if (key.startsWith('@_') || key === '#text' || key === ':@') continue;
      const lower = key.toLowerCase();
      if (['b', 'strong', 'i', 'em', 'u', 's', 'strike', 'span'].includes(lower)) {
        return true;
      }
      // Check children
      const children = nodeObj[key] as unknown[];
      if (Array.isArray(children) && hasInlineFormatting(children)) {
        return true;
      }
    }
  }
  return false;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Create a docx TextRun from our TextRunData
 */
function createDocxTextRun(run: TextRunData): DocxTextRun {
  const highlight = run.highlight ? (HIGHLIGHT_COLOR_MAP[run.highlight.toLowerCase()] || undefined) : undefined;

  return new DocxTextRun({
    text: run.text,
    bold: run.bold,
    italics: run.italic,
    underline: run.underline ? { type: UnderlineType.SINGLE } : undefined,
    strike: run.strike,
    color: run.color,
    size: run.fontSize,
    font: run.font,
    highlight,
  });
}

/**
 * Create text runs from inline content
 */
function createTextRunsFromContent(content: unknown[], bold?: boolean, italic?: boolean): DocxTextRun[] {
  const hasFormatting = hasInlineFormatting(content);

  if (hasFormatting) {
    const runs = parseInlineContent(content);
    return runs.map(createDocxTextRun);
  }

  const text = getTextContent(content);
  if (text) {
    return [new DocxTextRun({ text, bold, italics: italic })];
  }
  return [];
}

/**
 * Get paragraph alignment
 */
function getAlignment(alignment?: string): typeof AlignmentType[keyof typeof AlignmentType] | undefined {
  if (!alignment) return undefined;
  return ALIGNMENT_MAP[alignment];
}

/**
 * Create border configuration
 */
function createBorder(style = 'single', size = 4, color = 'CCCCCC') {
  const borderStyleMap: Record<string, typeof BorderStyle[keyof typeof BorderStyle]> = {
    single: BorderStyle.SINGLE,
    double: BorderStyle.DOUBLE,
    dashed: BorderStyle.DASHED,
    dotted: BorderStyle.DOTTED,
    none: BorderStyle.NONE,
  };
  return {
    style: borderStyleMap[style] || BorderStyle.SINGLE,
    size,
    color,
  };
}

/**
 * Convert data URL to buffer for image embedding
 */
function dataUrlToBuffer(dataUrl: string): Buffer | Uint8Array {
  const base64Match = dataUrl.match(/^data:[^;]+;base64,(.+)$/);
  if (base64Match) {
    const base64 = base64Match[1];
    // Browser environment
    if (typeof atob === 'function') {
      const binaryString = atob(base64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return bytes;
    }
    // Node environment
    return Buffer.from(base64, 'base64');
  }
  throw new Error('Invalid data URL format');
}

// ============================================================================
// XML Element Builders (create docx classes directly from XML nodes)
// ============================================================================

/**
 * Build a heading paragraph from XML node
 */
function buildHeadingFromXml(content: unknown[], attrs: Record<string, string>): Paragraph {
  const level = parseInt(attrs['@_level'] || '1', 10);
  const alignment = attrs['@_alignment'];
  const textRuns = createTextRunsFromContent(content);

  return new Paragraph({
    heading: HEADING_LEVEL_MAP[level] || HeadingLevel.HEADING_1,
    children: textRuns,
    alignment: alignment ? getAlignment(alignment) : undefined,
  });
}

/**
 * Build a paragraph from XML node
 */
function buildParagraphFromXml(content: unknown[], attrs: Record<string, string>): Paragraph {
  const alignment = attrs['@_alignment'];
  const textRuns = createTextRunsFromContent(content);

  return new Paragraph({
    children: textRuns,
    alignment: alignment ? getAlignment(alignment) : undefined,
  });
}

/**
 * Build bullet list items from XML node
 */
function buildBulletsFromXml(content: unknown[]): Paragraph[] {
  const paragraphs: Paragraph[] = [];

  for (const node of content) {
    if (!node || typeof node !== 'object') continue;
    const nodeObj = node as Record<string, unknown>;

    if ('item' in nodeObj) {
      const itemContent = nodeObj['item'] as unknown[];
      const itemAttrs = (nodeObj[':@'] || {}) as Record<string, string>;
      const level = parseInt(itemAttrs['@_level'] || '0', 10);
      const textRuns = createTextRunsFromContent(itemContent);

      paragraphs.push(new Paragraph({
        children: textRuns,
        bullet: { level },
      }));
    }
  }

  return paragraphs;
}

/**
 * Build numbered list items from XML node
 */
function buildNumberedFromXml(content: unknown[], numberRef: string): Paragraph[] {
  const paragraphs: Paragraph[] = [];

  for (const node of content) {
    if (!node || typeof node !== 'object') continue;
    const nodeObj = node as Record<string, unknown>;

    if ('item' in nodeObj) {
      const itemContent = nodeObj['item'] as unknown[];
      const itemAttrs = (nodeObj[':@'] || {}) as Record<string, string>;
      const level = parseInt(itemAttrs['@_level'] || '0', 10);
      const textRuns = createTextRunsFromContent(itemContent);

      paragraphs.push(new Paragraph({
        children: textRuns,
        numbering: {
          reference: numberRef,
          level,
          instance: paragraphs.length === 0 ? undefined : 0,
        },
      }));
    }
  }

  return paragraphs;
}

/**
 * Build a table cell from XML node
 */
function buildTableCellFromXml(
  content: unknown[],
  attrs: Record<string, string>,
  width: number,
  isHeader: boolean,
  tableAttrs: Record<string, string>
): DocxTableCell {
  const textRuns = createTextRunsFromContent(content, isHeader);
  const cellAlignment = attrs['@_align'];
  const cellBgColor = attrs['@_background'];
  const colspan = attrs['@_colspan'] ? parseInt(attrs['@_colspan'], 10) : undefined;
  const rowspan = attrs['@_rowspan'] ? parseInt(attrs['@_rowspan'], 10) : undefined;

  const alignment = cellAlignment || (isHeader ? 'center' : undefined);

  // Determine background color
  let backgroundColor: string | undefined;
  if (cellBgColor) {
    backgroundColor = cellBgColor.replace('#', '');
  } else if (isHeader) {
    backgroundColor = tableAttrs['@_headerBackground']?.replace('#', '') || DEFAULT_HEADER_BACKGROUND;
  }

  // Create border
  const borderColor = tableAttrs['@_border']?.replace('#', '') || DEFAULT_TABLE_BORDER.color;
  const border = createBorder('single', DEFAULT_TABLE_BORDER.size, borderColor);
  const borders = { top: border, bottom: border, left: border, right: border };

  return new DocxTableCell({
    width: { size: width, type: WidthType.DXA },
    children: [new Paragraph({
      children: textRuns,
      alignment: alignment ? getAlignment(alignment) : undefined,
    })],
    borders,
    margins: DEFAULT_CELL_PADDING,
    shading: backgroundColor ? { fill: backgroundColor, type: ShadingType.CLEAR } : undefined,
    columnSpan: colspan,
    rowSpan: rowspan,
  });
}

/**
 * Build a table from XML node
 */
function buildTableFromXml(content: unknown[], attrs: Record<string, string>): Table {
  const tableWidth = CONTENT_WIDTH;

  // Parse column widths
  const widthsStr = attrs['@_widths'];
  let columnWidths: number[] = [];

  // First pass: count columns from header row
  let columnCount = 0;
  for (const node of content) {
    if (!node || typeof node !== 'object') continue;
    const nodeObj = node as Record<string, unknown>;
    if ('header' in nodeObj) {
      const headerContent = nodeObj['header'] as unknown[];
      columnCount = headerContent.filter(n => n && typeof n === 'object' && 'cell' in (n as Record<string, unknown>)).length;
      break;
    }
  }

  if (widthsStr) {
    columnWidths = parsePercentageWidths(widthsStr, tableWidth);
  } else if (columnCount > 0) {
    // Equal distribution
    const colWidth = Math.floor(tableWidth / columnCount);
    columnWidths = Array(columnCount).fill(colWidth);
    const totalWidth = colWidth * columnCount;
    if (totalWidth < tableWidth) {
      columnWidths[columnCount - 1] += (tableWidth - totalWidth);
    }
  }

  const rows: DocxTableRow[] = [];

  for (const node of content) {
    if (!node || typeof node !== 'object') continue;
    const nodeObj = node as Record<string, unknown>;

    // Header row
    if ('header' in nodeObj) {
      const headerContent = nodeObj['header'] as unknown[];
      const cells: DocxTableCell[] = [];
      let idx = 0;

      for (const cellNode of headerContent) {
        if (!cellNode || typeof cellNode !== 'object') continue;
        const cellObj = cellNode as Record<string, unknown>;
        if ('cell' in cellObj) {
          const cellContent = cellObj['cell'] as unknown[];
          const cellAttrs = (cellObj[':@'] || {}) as Record<string, string>;
          cells.push(buildTableCellFromXml(cellContent, cellAttrs, columnWidths[idx] || colWidth(), true, attrs));
          idx++;
        }
      }

      rows.push(new DocxTableRow({ children: cells }));
    }

    // Data row
    if ('row' in nodeObj) {
      const rowContent = nodeObj['row'] as unknown[];
      const rowAttrs = (nodeObj[':@'] || {}) as Record<string, string>;
      const isHeaderRow = rowAttrs['@_header'] === 'true';
      const cells: DocxTableCell[] = [];
      let idx = 0;

      for (const cellNode of rowContent) {
        if (!cellNode || typeof cellNode !== 'object') continue;
        const cellObj = cellNode as Record<string, unknown>;
        if ('cell' in cellObj) {
          const cellContent = cellObj['cell'] as unknown[];
          const cellAttrs = (cellObj[':@'] || {}) as Record<string, string>;
          cells.push(buildTableCellFromXml(cellContent, cellAttrs, columnWidths[idx] || colWidth(), isHeaderRow, attrs));
          idx++;
        }
      }

      rows.push(new DocxTableRow({ children: cells }));
    }
  }

  function colWidth(): number {
    return Math.floor(tableWidth / Math.max(1, columnCount));
  }

  return new Table({
    width: { size: tableWidth, type: WidthType.DXA },
    columnWidths,
    rows,
  });
}

/**
 * Build an image from XML node
 */
function buildImageFromXml(attrs: Record<string, string>): Paragraph {
  const src = attrs['@_src'] || '';
  const format = attrs['@_format'] || 'png';
  const width = attrs['@_width'] ? parseInt(attrs['@_width'], 10) : 200;
  const height = attrs['@_height'] ? parseInt(attrs['@_height'], 10) : 150;
  const alignment = attrs['@_align'];
  const alt = attrs['@_alt'];

  if (!src.startsWith('data:')) {
    throw new Error('Image file paths are not supported in browser. Use data URLs instead.');
  }

  const imageData = dataUrlToBuffer(src);
  const imageFormat: 'png' | 'jpg' | 'gif' | 'bmp' = format === 'jpeg' ? 'jpg' : format as 'png' | 'jpg' | 'gif' | 'bmp';

  const imageRun = new ImageRun({
    type: imageFormat,
    data: imageData,
    transformation: { width, height },
    altText: alt ? { title: alt, description: alt, name: alt } : undefined,
  });

  return new Paragraph({
    children: [imageRun],
    alignment: alignment ? getAlignment(alignment) : undefined,
  });
}

/**
 * Build a page break
 */
function buildPageBreakFromXml(): Paragraph {
  return new Paragraph({
    children: [new PageBreak()],
  });
}

/**
 * Create docx elements from an XML element node
 */
function createElementsFromXmlNode(
  node: Record<string, unknown>,
  numberRef: string
): (Paragraph | Table)[] {
  const attrs = (node[':@'] || {}) as Record<string, string>;

  if ('heading' in node) {
    return [buildHeadingFromXml(node['heading'] as unknown[], attrs)];
  }
  if ('paragraph' in node || 'p' in node) {
    const key = 'paragraph' in node ? 'paragraph' : 'p';
    return [buildParagraphFromXml(node[key] as unknown[], attrs)];
  }
  if ('bullets' in node) {
    return buildBulletsFromXml(node['bullets'] as unknown[]);
  }
  if ('numbered' in node) {
    return buildNumberedFromXml(node['numbered'] as unknown[], numberRef);
  }
  if ('table' in node) {
    return [buildTableFromXml(node['table'] as unknown[], attrs)];
  }
  if ('image' in node) {
    return [buildImageFromXml(attrs)];
  }
  if ('pageBreak' in node || 'pagebreak' in node) {
    return [buildPageBreakFromXml()];
  }

  return [];
}

// ============================================================================
// XML Section Builders
// ============================================================================

/**
 * Parse header/footer elements from XML
 */
function parseHeaderFooterElements(content: unknown[]): Array<{ type: string; text?: string; bold?: boolean; italic?: boolean }> {
  const elements: Array<{ type: string; text?: string; bold?: boolean; italic?: boolean }> = [];

  for (const node of content) {
    if (!node || typeof node !== 'object') continue;
    const nodeObj = node as Record<string, unknown>;

    if ('#text' in nodeObj) {
      const text = String(nodeObj['#text']);
      if (text) elements.push({ type: 'text', text });
      continue;
    }

    if ('text' in nodeObj) {
      const textContent = nodeObj['text'] as unknown[];
      const textAttrs = (nodeObj[':@'] || {}) as Record<string, string>;
      elements.push({
        type: 'text',
        text: getTextContent(textContent),
        bold: textAttrs['@_bold'] === 'true',
        italic: textAttrs['@_italic'] === 'true',
      });
    }

    if ('pageNumber' in nodeObj || 'pagenumber' in nodeObj) {
      elements.push({ type: 'pageNumber' });
    }
  }

  return elements;
}

/**
 * Build header from XML node
 */
function buildHeaderFromXml(content: unknown[], attrs: Record<string, string>): Header {
  const elements = parseHeaderFooterElements(content);
  const alignment = attrs['@_alignment'];
  const textRuns: DocxTextRun[] = [];

  for (const el of elements) {
    if (el.type === 'text') {
      textRuns.push(new DocxTextRun({ text: el.text || '', bold: el.bold, italics: el.italic }));
    } else if (el.type === 'pageNumber') {
      textRuns.push(new DocxTextRun({ children: [PageNumber.CURRENT] }));
    }
  }

  return new Header({
    children: [new Paragraph({
      children: textRuns,
      alignment: alignment ? getAlignment(alignment) : AlignmentType.CENTER,
    })],
  });
}

/**
 * Build footer from XML node
 */
function buildFooterFromXml(content: unknown[], attrs: Record<string, string>): Footer {
  const elements = parseHeaderFooterElements(content);
  const alignment = attrs['@_alignment'];
  const textRuns: DocxTextRun[] = [];

  for (const el of elements) {
    if (el.type === 'text') {
      textRuns.push(new DocxTextRun({ text: el.text || '', bold: el.bold, italics: el.italic }));
    } else if (el.type === 'pageNumber') {
      textRuns.push(new DocxTextRun({ children: [PageNumber.CURRENT] }));
    }
  }

  return new Footer({
    children: [new Paragraph({
      children: textRuns,
      alignment: alignment ? getAlignment(alignment) : AlignmentType.CENTER,
    })],
  });
}

/**
 * Build section properties from XML config
 */
function buildSectionPropertiesFromXml(
  _docAttrs: Record<string, string>,
  sectionAttrs: Record<string, string>,
  configNode?: unknown[]
): ISectionOptions['properties'] {
  let margins = { top: DEFAULT_MARGIN, right: DEFAULT_MARGIN, bottom: DEFAULT_MARGIN, left: DEFAULT_MARGIN };
  let orientation: 'portrait' | 'landscape' = 'portrait';

  // Parse config node if present
  if (configNode) {
    for (const node of configNode) {
      if (!node || typeof node !== 'object') continue;
      const nodeObj = node as Record<string, unknown>;
      const nodeAttrs = (nodeObj[':@'] || {}) as Record<string, string>;

      if ('margins' in nodeObj) {
        margins = {
          top: parseUnit(nodeAttrs['@_top'], DEFAULT_MARGIN),
          right: parseUnit(nodeAttrs['@_right'], DEFAULT_MARGIN),
          bottom: parseUnit(nodeAttrs['@_bottom'], DEFAULT_MARGIN),
          left: parseUnit(nodeAttrs['@_left'], DEFAULT_MARGIN),
        };
      }
    }
  }

  // Check section attrs for orientation
  if (sectionAttrs['@_orientation']) {
    orientation = sectionAttrs['@_orientation'] as 'portrait' | 'landscape';
  }

  return {
    page: {
      size: {
        width: US_LETTER_WIDTH,
        height: US_LETTER_HEIGHT,
        orientation: orientation === 'landscape' ? PageOrientation.LANDSCAPE : PageOrientation.PORTRAIT,
      },
      margin: margins,
    },
  };
}

// ============================================================================
// Document Styles Builder
// ============================================================================

function buildDocumentStyles(): IStylesOptions {
  const paragraphStyles = [];

  for (let level = 1; level <= 6; level++) {
    const defaultStyle = DEFAULT_HEADING_STYLES[level];

    paragraphStyles.push({
      id: `Heading${level}`,
      name: `Heading ${level}`,
      basedOn: 'Normal',
      next: 'Normal',
      quickFormat: true,
      run: {
        font: defaultStyle.font,
        size: defaultStyle.size,
        bold: defaultStyle.bold,
      },
      paragraph: {
        spacing: {
          before: defaultStyle.spacing.before,
          after: defaultStyle.spacing.after,
        },
        outlineLevel: level - 1,
      },
    });
  }

  return {
    default: {
      document: {
        run: {
          font: DEFAULT_FONT,
          size: DEFAULT_SIZE,
        },
      },
    },
    paragraphStyles,
  };
}

// ============================================================================
// Numbering Configuration Builder
// ============================================================================

function buildNumberingConfig(): INumberingOptions {
  return {
    config: [
      {
        reference: 'default-bullets',
        levels: [{
          level: 0,
          format: LevelFormat.BULLET,
          text: '\u2022',
          alignment: AlignmentType.LEFT,
          style: {
            paragraph: {
              indent: { left: 720, hanging: 360 },
            },
          },
        }],
      },
      {
        reference: 'default-numbering',
        levels: [{
          level: 0,
          format: LevelFormat.DECIMAL,
          text: '%1.',
          alignment: AlignmentType.START,
          style: {
            paragraph: {
              indent: { left: convertInchesToTwip(0.5), hanging: convertInchesToTwip(0.25) },
            },
          },
        }],
      },
    ],
  };
}

// ============================================================================
// Main XML Document Builder
// ============================================================================

/**
 * Build a docx Document directly from XML string
 * This is the new direct XML-to-docx path - no intermediate DocumentJSON
 */
export function buildDocxFromXml(xmlString: string): Document {
  const parser = new XMLParser(xmlParserOptions);
  const parsed = parser.parse(xmlString);

  if (!Array.isArray(parsed) || parsed.length === 0) {
    throw new Error('Invalid XML structure: no root element found');
  }

  // Find the document root
  let documentNode: unknown[] | null = null;
  let documentAttrs: Record<string, string> = {};

  for (const node of parsed) {
    if (!node || typeof node !== 'object') continue;
    const nodeObj = node as Record<string, unknown>;

    if ('document' in nodeObj) {
      documentNode = nodeObj['document'] as unknown[];
      documentAttrs = (nodeObj[':@'] || {}) as Record<string, string>;
      break;
    }
  }

  if (!documentNode) {
    throw new Error('Missing <document> root element');
  }

  const numberRef = 'default-numbering';
  const sections: ISectionOptions[] = [];
  let configNode: unknown[] | undefined;
  let title: string | undefined = documentAttrs['@_title'];

  // First pass: find config
  for (const node of documentNode) {
    if (!node || typeof node !== 'object') continue;
    const nodeObj = node as Record<string, unknown>;

    if ('config' in nodeObj) {
      configNode = nodeObj['config'] as unknown[];
    }
  }

  // Check if we have explicit sections
  let hasExplicitSections = false;
  for (const node of documentNode) {
    if (!node || typeof node !== 'object') continue;
    const nodeObj = node as Record<string, unknown>;
    if ('section' in nodeObj) {
      hasExplicitSections = true;
      break;
    }
  }

  if (hasExplicitSections) {
    // Parse explicit sections
    for (const node of documentNode) {
      if (!node || typeof node !== 'object') continue;
      const nodeObj = node as Record<string, unknown>;
      const nodeAttrs = (nodeObj[':@'] || {}) as Record<string, string>;

      if ('section' in nodeObj) {
        const sectionContent = nodeObj['section'] as unknown[];
        const children: (Paragraph | Table)[] = [];
        let sectionHeader: Header | undefined;
        let sectionFooter: Footer | undefined;

        // Add title as first heading in first section
        if (sections.length === 0 && title) {
          children.push(new Paragraph({
            text: title,
            heading: HeadingLevel.TITLE,
            spacing: { after: 400 },
          }));
        }

        for (const sectionNode of sectionContent) {
          if (!sectionNode || typeof sectionNode !== 'object') continue;
          const sectionNodeObj = sectionNode as Record<string, unknown>;
          const sectionNodeAttrs = (sectionNodeObj[':@'] || {}) as Record<string, string>;

          if ('header' in sectionNodeObj) {
            sectionHeader = buildHeaderFromXml(sectionNodeObj['header'] as unknown[], sectionNodeAttrs);
          } else if ('footer' in sectionNodeObj) {
            sectionFooter = buildFooterFromXml(sectionNodeObj['footer'] as unknown[], sectionNodeAttrs);
          } else {
            children.push(...createElementsFromXmlNode(sectionNodeObj, numberRef));
          }
        }

        sections.push({
          properties: buildSectionPropertiesFromXml(documentAttrs, nodeAttrs, configNode),
          headers: sectionHeader ? { default: sectionHeader } : undefined,
          footers: sectionFooter ? { default: sectionFooter } : {
            default: new Footer({
              children: [new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [new DocxTextRun({ children: [PageNumber.CURRENT] })],
              })],
            }),
          },
          children,
        });
      }
    }
  } else {
    // No explicit sections - create a default section with content elements
    const children: (Paragraph | Table)[] = [];

    // Add title if present
    if (title) {
      children.push(new Paragraph({
        text: title,
        heading: HeadingLevel.TITLE,
        spacing: { after: 400 },
      }));
    }

    for (const node of documentNode) {
      if (!node || typeof node !== 'object') continue;
      const nodeObj = node as Record<string, unknown>;

      // Skip config
      if ('config' in nodeObj) continue;

      children.push(...createElementsFromXmlNode(nodeObj, numberRef));
    }

    sections.push({
      properties: buildSectionPropertiesFromXml(documentAttrs, {}, configNode),
      footers: {
        default: new Footer({
          children: [new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [new DocxTextRun({ children: [PageNumber.CURRENT] })],
          })],
        }),
      },
      children,
    });
  }

  return new Document({
    styles: buildDocumentStyles(),
    numbering: buildNumberingConfig(),
    sections,
  });
}

// ============================================================================
// Document Summary from XML
// ============================================================================

export interface DocumentSummary {
  title: string;
  sectionCount: number;
  paragraphCount: number;
  tableCount: number;
  listCount: number;
  imageCount: number;
  hasTableOfContents: boolean;
}

/**
 * Get document summary directly from XML string
 */
export function getDocumentSummaryFromXml(xmlString: string): DocumentSummary {
  try {
    const parser = new XMLParser(xmlParserOptions);
    const parsed = parser.parse(xmlString);

    if (!Array.isArray(parsed) || parsed.length === 0) {
      return defaultSummary();
    }

    // Find document root
    let documentNode: unknown[] | null = null;
    let documentAttrs: Record<string, string> = {};

    for (const node of parsed) {
      if (!node || typeof node !== 'object') continue;
      const nodeObj = node as Record<string, unknown>;

      if ('document' in nodeObj) {
        documentNode = nodeObj['document'] as unknown[];
        documentAttrs = (nodeObj[':@'] || {}) as Record<string, string>;
        break;
      }
    }

    if (!documentNode) {
      return defaultSummary();
    }

    const title = documentAttrs['@_title'] || 'Untitled Document';
    let sectionCount = 0;
    let paragraphCount = 0;
    let tableCount = 0;
    let listCount = 0;
    let imageCount = 0;
    let hasTableOfContents = false;

    function countElements(nodes: unknown[]) {
      for (const node of nodes) {
        if (!node || typeof node !== 'object') continue;
        const nodeObj = node as Record<string, unknown>;

        if ('heading' in nodeObj) paragraphCount++;
        if ('paragraph' in nodeObj || 'p' in nodeObj) paragraphCount++;
        if ('table' in nodeObj) tableCount++;
        if ('bullets' in nodeObj || 'numbered' in nodeObj) listCount++;
        if ('image' in nodeObj) imageCount++;
        if ('tableOfContents' in nodeObj || 'toc' in nodeObj) hasTableOfContents = true;

        // Check for sections
        if ('section' in nodeObj) {
          sectionCount++;
          countElements(nodeObj['section'] as unknown[]);
        }
      }
    }

    countElements(documentNode);

    // If no explicit sections, count as 1
    if (sectionCount === 0 && (paragraphCount > 0 || tableCount > 0 || listCount > 0)) {
      sectionCount = 1;
    }

    return {
      title,
      sectionCount,
      paragraphCount,
      tableCount,
      listCount,
      imageCount,
      hasTableOfContents,
    };
  } catch {
    return defaultSummary();
  }
}

function defaultSummary(): DocumentSummary {
  return {
    title: 'Untitled Document',
    sectionCount: 0,
    paragraphCount: 0,
    tableCount: 0,
    listCount: 0,
    imageCount: 0,
    hasTableOfContents: false,
  };
}

// ============================================================================
// Export Functions
// ============================================================================

/**
 * Download document from XML string as .docx file
 */
export async function downloadDocxFromXml(xmlString: string, filename?: string): Promise<void> {
  console.log('[downloadDocxFromXml] Building docx from XML, length:', xmlString.length);

  const doc = buildDocxFromXml(xmlString);
  const blob = await Packer.toBlob(doc);

  console.log('[downloadDocxFromXml] Blob size:', blob.size, 'Type:', blob.type);

  const docxBlob = new Blob([blob], {
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });

  const finalFilename = filename || 'document';
  const sanitizedFilename = finalFilename.replace(/[^a-zA-Z0-9\s-_]/g, '').trim() || 'document';

  console.log('[downloadDocxFromXml] Saving as:', `${sanitizedFilename}.docx`);
  safeSaveAs(docxBlob, `${sanitizedFilename}.docx`);
}

// ============================================================================
// XML Input Detection
// ============================================================================

/**
 * Check if input is XML (starts with < after trimming whitespace)
 */
export function isXmlInput(input: string): boolean {
  const trimmed = input.trim();
  return trimmed.startsWith('<');
}

// ============================================================================
// DocumentJSON to XML Conversion (for backward compatibility)
// ============================================================================

/**
 * Convert DocumentJSON to XML string.
 * @deprecated Use buildDocxFromJson() directly instead.
 */
export function documentJsonToXml(doc: DocumentJSON): string {
  const escapeXml = (str: string): string => {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  };

  const formatText = (text: string | undefined): string => {
    if (!text) return '';
    return escapeXml(text);
  };

  const formatListItem = (item: string | { text: string }): string => {
    if (typeof item === 'string') return escapeXml(item);
    return escapeXml(item.text || '');
  };

  const formatTableCell = (cell: string | { text?: string }): string => {
    if (typeof cell === 'string') return escapeXml(cell);
    return escapeXml(cell.text || '');
  };

  const elementToXml = (el: DocumentElement): string => {
    switch (el.type) {
      case 'heading':
        return `<heading level="${el.level || 1}">${formatText(el.text)}</heading>`;
      case 'paragraph':
        return `<paragraph>${formatText(el.text)}</paragraph>`;
      case 'bullets': {
        const items = (el.items || []).map((item: string | { text: string }) => `<item>${formatListItem(item)}</item>`).join('');
        return `<bullets>${items}</bullets>`;
      }
      case 'numbered': {
        const items = (el.items || []).map((item: string | { text: string }) => `<item>${formatListItem(item)}</item>`).join('');
        return `<numbered>${items}</numbered>`;
      }
      case 'table': {
        const rows = ((el as { rows?: Array<string[] | { cells: Array<string | { text?: string }> }> }).rows || []).map((row: string[] | { cells: Array<string | { text?: string }> }) => {
          const cells = Array.isArray(row) ? row : (row.cells || []);
          return `<row>${cells.map((cell: string | { text?: string }) => `<cell>${formatTableCell(cell)}</cell>`).join('')}</row>`;
        }).join('');
        return `<table>${rows}</table>`;
      }
      case 'image':
        return `<image src="${escapeXml(el.src || '')}" />`;
      case 'pageBreak':
        return '<pageBreak />';
      default:
        return '';
    }
  };

  const sectionToXml = (section: DocumentSection): string => {
    const elements = (section.children || []).map(elementToXml).join('\n    ');
    return `  <section>\n    ${elements}\n  </section>`;
  };

  const titleAttr = doc.title ? ` title="${escapeXml(doc.title)}"` : '';
  const sections = (doc.sections || []).map(sectionToXml).join('\n');
  return `<document${titleAttr}>\n${sections}\n</document>`;
}

// ============================================================================
// JSON Document Builder (DocumentJSON → docx Document)
// ============================================================================

import type {
  TextRun as TextRunType,
  BulletItem,
  NumberedItem,
  TableCell as TableCellType,
  TableRow as TableRowType,
  HeadingElement,
  ParagraphElement,
  BulletsElement,
  NumberedElement,
  TableElement,
  ImageElement,
} from '../types';

/**
 * Create docx TextRuns from a DocumentJSON TextRun array
 */
function createTextRunsFromJson(runs: TextRunType[]): DocxTextRun[] {
  return runs.map(run => {
    const highlight = run.highlight ? (HIGHLIGHT_COLOR_MAP[run.highlight.toLowerCase()] || undefined) : undefined;
    return new DocxTextRun({
      text: run.text,
      bold: run.bold,
      italics: run.italic,
      underline: run.underline ? { type: UnderlineType.SINGLE } : undefined,
      strike: run.strike,
      color: run.color,
      size: run.fontSize,
      font: run.font,
      highlight,
    });
  });
}

/**
 * Build a heading paragraph from HeadingElement
 */
function buildHeadingFromJson(el: HeadingElement): Paragraph {
  const children = el.runs
    ? createTextRunsFromJson(el.runs)
    : [new DocxTextRun({ text: el.text || '' })];

  return new Paragraph({
    heading: HEADING_LEVEL_MAP[el.level] || HeadingLevel.HEADING_1,
    children,
    alignment: el.format?.alignment ? getAlignment(el.format.alignment) : undefined,
  });
}

/**
 * Build a paragraph from ParagraphElement
 */
function buildParagraphFromJson(el: ParagraphElement): Paragraph {
  let children: DocxTextRun[];
  if (el.runs) {
    children = createTextRunsFromJson(el.runs);
  } else {
    children = [new DocxTextRun({
      text: el.text || '',
      bold: el.bold,
      italics: el.italic,
    })];
  }

  return new Paragraph({
    children,
    alignment: el.format?.alignment ? getAlignment(el.format.alignment) : undefined,
    spacing: el.format?.spacing ? {
      before: el.format.spacing.before,
      after: el.format.spacing.after,
      line: el.format.spacing.line,
    } : undefined,
    indent: el.format?.indent ? {
      left: el.format.indent.left,
      right: el.format.indent.right,
      firstLine: el.format.indent.firstLine && el.format.indent.firstLine > 0 ? el.format.indent.firstLine : undefined,
      hanging: el.format.indent.firstLine && el.format.indent.firstLine < 0 ? -el.format.indent.firstLine : undefined,
    } : undefined,
  });
}

/**
 * Build bullet list paragraphs from BulletsElement
 */
function buildBulletsFromJson(el: BulletsElement): Paragraph[] {
  return (el.items || []).map(item => {
    const isObj = typeof item === 'object';
    const text = isObj ? (item as BulletItem).text : (item as string);
    const level = isObj ? ((item as BulletItem).level || 0) : 0;
    const runs = isObj && (item as BulletItem).runs;

    const children = runs
      ? createTextRunsFromJson(runs)
      : [new DocxTextRun({ text })];

    return new Paragraph({ children, bullet: { level } });
  });
}

/**
 * Build numbered list paragraphs from NumberedElement
 */
function buildNumberedFromJson(el: NumberedElement, numberRef: string): Paragraph[] {
  return (el.items || []).map((item, idx) => {
    const isObj = typeof item === 'object';
    const text = isObj ? (item as NumberedItem).text : (item as string);
    const level = isObj ? ((item as NumberedItem).level || 0) : 0;
    const runs = isObj && (item as NumberedItem).runs;

    const children = runs
      ? createTextRunsFromJson(runs)
      : [new DocxTextRun({ text })];

    return new Paragraph({
      children,
      numbering: { reference: numberRef, level, instance: idx === 0 ? undefined : 0 },
    });
  });
}

/**
 * Build a table cell from TableCellType or string
 */
function buildTableCellFromJson(
  cell: string | TableCellType,
  width: number,
  isHeader: boolean,
  format?: import('../types').TableFormat,
): DocxTableCell {
  const isObj = typeof cell === 'object';
  const text = isObj ? (cell as TableCellType).text || '' : (cell as string);
  const cellAlignment = isObj ? (cell as TableCellType).alignment : undefined;
  const cellBgColor = isObj ? (cell as TableCellType).backgroundColor : undefined;
  const colspan = isObj ? (cell as TableCellType).colspan : undefined;
  const rowspan = isObj ? (cell as TableCellType).rowspan : undefined;
  const runs = isObj ? (cell as TableCellType).runs : undefined;

  const alignment = cellAlignment || (isHeader ? 'center' : undefined);

  let backgroundColor: string | undefined;
  if (cellBgColor) {
    backgroundColor = cellBgColor.replace('#', '');
  } else if (isHeader) {
    backgroundColor = format?.headerBackground?.replace('#', '') || DEFAULT_HEADER_BACKGROUND;
  }

  const borderColor = format?.borders?.color?.replace('#', '') || DEFAULT_TABLE_BORDER.color;
  const borderSize = format?.borders?.size || DEFAULT_TABLE_BORDER.size;
  const borderStyle = format?.borders?.style || 'single';
  const border = createBorder(borderStyle, borderSize, borderColor);
  const borders = { top: border, bottom: border, left: border, right: border };

  const padding = format?.cellPadding || DEFAULT_CELL_PADDING;

  const children = runs
    ? createTextRunsFromJson(runs)
    : [new DocxTextRun({ text, bold: isHeader })];

  return new DocxTableCell({
    width: { size: width, type: WidthType.DXA },
    children: [new Paragraph({
      children,
      alignment: alignment ? getAlignment(alignment) : undefined,
    })],
    borders,
    margins: padding,
    shading: backgroundColor ? { fill: backgroundColor, type: ShadingType.CLEAR } : undefined,
    columnSpan: colspan,
    rowSpan: rowspan,
  });
}

/**
 * Build a table from TableElement
 */
function buildTableFromJson(el: TableElement): Table {
  const tableWidth = el.format?.width || CONTENT_WIDTH;
  const columnCount = (el.headers || []).length;

  // Column widths
  let columnWidths: number[];
  if (el.columns && el.columns.length > 0) {
    columnWidths = el.columns.map(c => c.width);
  } else if (columnCount > 0) {
    const colWidth = Math.floor(tableWidth / columnCount);
    columnWidths = Array(columnCount).fill(colWidth);
    const totalWidth = colWidth * columnCount;
    if (totalWidth < tableWidth) {
      columnWidths[columnCount - 1] += (tableWidth - totalWidth);
    }
  } else {
    columnWidths = [];
  }

  const rows: DocxTableRow[] = [];

  // Header row
  if (el.headers && el.headers.length > 0) {
    const headerCells = el.headers.map((cell, idx) =>
      buildTableCellFromJson(cell, columnWidths[idx] || Math.floor(tableWidth / columnCount), true, el.format)
    );
    rows.push(new DocxTableRow({ children: headerCells }));
  }

  // Data rows
  for (const row of (el.rows || [])) {
    const isEnhanced = !Array.isArray(row) && typeof row === 'object' && 'cells' in row;
    const cells = isEnhanced ? (row as TableRowType).cells : (row as string[]);
    const isHeaderRow = isEnhanced ? !!(row as TableRowType).isHeader : false;

    const tableCells = cells.map((cell, idx) =>
      buildTableCellFromJson(cell, columnWidths[idx] || Math.floor(tableWidth / Math.max(1, columnCount)), isHeaderRow, el.format)
    );
    rows.push(new DocxTableRow({ children: tableCells }));
  }

  return new Table({
    width: { size: tableWidth, type: WidthType.DXA },
    columnWidths,
    rows,
  });
}

/**
 * Build an image paragraph from ImageElement
 */
function buildImageFromJson(el: ImageElement): Paragraph {
  const width = el.width || 200;
  const height = el.height || 150;

  if (!el.src || !el.src.startsWith('data:')) {
    throw new Error('Image file paths are not supported in browser. Use data URLs instead.');
  }

  const imageData = dataUrlToBuffer(el.src);
  const imageFormat: 'png' | 'jpg' | 'gif' | 'bmp' = el.format === 'jpeg' ? 'jpg' : (el.format || 'png') as 'png' | 'jpg' | 'gif' | 'bmp';

  return new Paragraph({
    children: [new ImageRun({
      type: imageFormat,
      data: imageData,
      transformation: { width, height },
      altText: el.altText ? { title: el.altText.title, description: el.altText.description, name: el.altText.name || el.altText.title } : undefined,
    })],
    alignment: el.alignment ? getAlignment(el.alignment) : undefined,
  });
}

/**
 * Create docx elements from a DocumentElement
 */
function createElementsFromJson(el: DocumentElement, numberRef: string): (Paragraph | Table)[] {
  switch (el.type) {
    case 'heading':
      return [buildHeadingFromJson(el)];
    case 'paragraph':
      return [buildParagraphFromJson(el)];
    case 'bullets':
      return buildBulletsFromJson(el);
    case 'numbered':
      return buildNumberedFromJson(el, numberRef);
    case 'table':
      return [buildTableFromJson(el)];
    case 'image':
      return [buildImageFromJson(el)];
    case 'pageBreak':
      return [new Paragraph({ children: [new PageBreak()] })];
    default:
      return [];
  }
}

/**
 * Build header from SectionHeader JSON
 */
function buildHeaderFromJson(header: import('../types').SectionHeader): Header {
  const textRuns: DocxTextRun[] = [];
  for (const el of header.elements) {
    if (el.type === 'text') {
      textRuns.push(new DocxTextRun({ text: el.text || '', bold: el.bold, italics: el.italic }));
    } else if (el.type === 'pageNumber') {
      textRuns.push(new DocxTextRun({ children: [PageNumber.CURRENT] }));
    }
  }
  return new Header({
    children: [new Paragraph({
      children: textRuns,
      alignment: header.alignment ? getAlignment(header.alignment) : AlignmentType.CENTER,
    })],
  });
}

/**
 * Build footer from SectionFooter JSON
 */
function buildFooterFromJson(footer: import('../types').SectionFooter): Footer {
  const textRuns: DocxTextRun[] = [];
  for (const el of footer.elements) {
    if (el.type === 'text') {
      textRuns.push(new DocxTextRun({ text: el.text || '', bold: el.bold, italics: el.italic }));
    } else if (el.type === 'pageNumber') {
      textRuns.push(new DocxTextRun({ children: [PageNumber.CURRENT] }));
    }
  }
  return new Footer({
    children: [new Paragraph({
      children: textRuns,
      alignment: footer.alignment ? getAlignment(footer.alignment) : AlignmentType.CENTER,
    })],
  });
}

/**
 * Build section properties from DocumentJSON config
 */
function buildSectionPropertiesFromJson(
  docConfig?: import('../types').DocumentConfig,
  sectionConfig?: import('../types').SectionConfig,
): ISectionOptions['properties'] {
  const config = sectionConfig || docConfig;
  const margins = {
    top: config?.margins?.top ?? docConfig?.margins?.top ?? DEFAULT_MARGIN,
    right: config?.margins?.right ?? docConfig?.margins?.right ?? DEFAULT_MARGIN,
    bottom: config?.margins?.bottom ?? docConfig?.margins?.bottom ?? DEFAULT_MARGIN,
    left: config?.margins?.left ?? docConfig?.margins?.left ?? DEFAULT_MARGIN,
  };

  const orientation = sectionConfig?.orientation || docConfig?.orientation || 'portrait';
  const pageWidth = config?.pageSize?.width ?? docConfig?.pageSize?.width ?? US_LETTER_WIDTH;
  const pageHeight = config?.pageSize?.height ?? docConfig?.pageSize?.height ?? US_LETTER_HEIGHT;

  return {
    page: {
      size: {
        width: pageWidth,
        height: pageHeight,
        orientation: orientation === 'landscape' ? PageOrientation.LANDSCAPE : PageOrientation.PORTRAIT,
      },
      margin: margins,
    },
  };
}

/**
 * Build a docx Document directly from DocumentJSON — no XML intermediate.
 */
export function buildDocxFromJson(doc: DocumentJSON): Document {
  const numberRef = 'default-numbering';
  const sections: ISectionOptions[] = [];

  for (let si = 0; si < doc.sections.length; si++) {
    const section = doc.sections[si];
    const children: (Paragraph | Table)[] = [];

    // Add title as first heading in first section
    if (si === 0 && doc.title) {
      children.push(new Paragraph({
        text: doc.title,
        heading: HeadingLevel.TITLE,
        spacing: { after: 400 },
      }));
    }

    // Build elements
    for (const el of section.children) {
      children.push(...createElementsFromJson(el, numberRef));
    }

    // Build header/footer
    const sectionHeader = section.header ? buildHeaderFromJson(section.header) : undefined;
    const sectionFooter = section.footer ? buildFooterFromJson(section.footer) : undefined;

    sections.push({
      properties: buildSectionPropertiesFromJson(doc.config, section.config),
      headers: sectionHeader ? { default: sectionHeader } : undefined,
      footers: sectionFooter ? { default: sectionFooter } : {
        default: new Footer({
          children: [new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [new DocxTextRun({ children: [PageNumber.CURRENT] })],
          })],
        }),
      },
      children,
    });
  }

  return new Document({
    styles: buildDocumentStyles(),
    numbering: buildNumberingConfig(),
    sections,
  });
}

/**
 * Get document summary directly from DocumentJSON
 */
export function getDocumentSummaryFromJson(doc: DocumentJSON): DocumentSummary {
  let paragraphCount = 0;
  let tableCount = 0;
  let listCount = 0;
  let imageCount = 0;

  for (const section of doc.sections) {
    for (const el of section.children) {
      switch (el.type) {
        case 'heading':
        case 'paragraph':
          paragraphCount++;
          break;
        case 'table':
          tableCount++;
          break;
        case 'bullets':
        case 'numbered':
          listCount++;
          break;
        case 'image':
          imageCount++;
          break;
      }
    }
  }

  return {
    title: doc.title || 'Untitled Document',
    sectionCount: doc.sections.length,
    paragraphCount,
    tableCount,
    listCount,
    imageCount,
    hasTableOfContents: !!doc.config?.tableOfContents?.enabled,
  };
}

/**
 * Download document from DocumentJSON as .docx file
 */
export async function downloadDocxFromJson(doc: DocumentJSON, filename?: string): Promise<void> {
  const docxDoc = buildDocxFromJson(doc);
  const blob = await Packer.toBlob(docxDoc);

  const docxBlob = new Blob([blob], {
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });

  const finalFilename = filename || doc.title || 'document';
  const sanitizedFilename = finalFilename.replace(/[^a-zA-Z0-9\s-_]/g, '').trim() || 'document';
  safeSaveAs(docxBlob, `${sanitizedFilename}.docx`);
}

/**
 * Get document summary from DocumentJSON (for uploaded files).
 * @deprecated Use getDocumentSummaryFromJson() directly.
 */
export function getDocumentSummary(doc: DocumentJSON): DocumentSummary {
  return getDocumentSummaryFromJson(doc);
}

/**
 * Download document from a stored DOCX binary (IDB).
 * Used after XML-based editing (repack_document).
 */
export async function downloadDocxFromBinary(documentId: string, filename?: string): Promise<void> {
  const { loadDocxBinary } = await import('../stores/contentStore');
  const binary = await loadDocxBinary(documentId);
  if (!binary) {
    throw new Error(`No DOCX binary found for document: ${documentId}`);
  }

  const blob = new Blob([binary], {
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });

  const finalFilename = filename || 'document';
  const sanitizedFilename = finalFilename.replace(/[^a-zA-Z0-9\s-_]/g, '').trim() || 'document';
  safeSaveAs(blob, `${sanitizedFilename}.docx`);
}

/**
 * Download document from DocumentJSON (for uploaded files).
 * @deprecated Use downloadDocxFromJson() directly.
 */
export async function downloadDocx(doc: DocumentJSON): Promise<void> {
  await downloadDocxFromJson(doc, doc.title);
}
